import { DateTime } from "luxon";

import type { LogEntry } from "@apis/calorie-grid/models";

import {
  FIGURE_OUTLINE_WIDTH,
  GRID_LINE_WIDTH,
  GRID_PAST_DAYS,
  GRID_ROWS,
} from "../../bootstrap/grid.ts";

const TODAY_ROW = GRID_ROWS - 1 - GRID_PAST_DAYS;

const FIGURE_INSET = (FIGURE_OUTLINE_WIDTH + GRID_LINE_WIDTH) / 2;

interface OutlinePoint {
  readonly x: number;
  readonly y: number;
}

interface GridShape {
  readonly head: OutlinePoint | undefined;
  readonly outline: string;
}

class ShapeManager {
  public marginalSquareCount(
    caloriesBefore: number,
    calories: number,
    squareKcal: number,
  ): number {
    if (squareKcal <= 0) {
      return 0;
    }

    return (
      Math.floor((caloriesBefore + calories) / squareKcal) -
      Math.floor(caloriesBefore / squareKcal)
    );
  }

  public nextSquaresPerRow(
    currentSquaresPerRow: number,
    squares: number,
    columns: number,
  ): number {
    return currentSquaresPerRow - 1 < 1
      ? Math.min(squares, columns)
      : currentSquaresPerRow - 1;
  }

  public squareCount(calories: number, squareKcal: number): number {
    return squareKcal <= 0 ? 0 : Math.floor(calories / squareKcal);
  }

  public toGridShape(logEntry: LogEntry, today: Date): GridShape {
    const startOfToday = DateTime.fromJSDate(today).startOf("day");
    const points = (logEntry.cells ?? []).map(cell => {
      const { days } = DateTime.fromISO(cell.date)
        .startOf("day")
        .diff(startOfToday, "days");

      return { x: cell.col, y: TODAY_ROW - Math.round(days) };
    });

    return { head: points[0], outline: this.outline(points) };
  }

  private outline(cells: readonly OutlinePoint[]): string {
    if (cells.length === 0) {
      return "";
    }

    const corners = insetCorners(traceCorners(cells), FIGURE_INSET);
    const [first, ...rest] = corners;

    if (!first) {
      return "";
    }

    return [
      `M ${first.x} ${first.y}`,
      ...rest.map(corner => `L ${corner.x} ${corner.y}`),
      "Z",
    ].join(" ");
  }
}

function insetCorners(
  corners: readonly OutlinePoint[],
  inset: number,
): readonly OutlinePoint[] {
  return corners.map((corner, index) => {
    const previous =
      corners[(index - 1 + corners.length) % corners.length] ?? corner;

    const next = corners[(index + 1) % corners.length] ?? corner;

    const incoming = inwardNormal(previous, corner);

    const outgoing = inwardNormal(corner, next);

    return {
      x: corner.x + inset * (incoming.x + outgoing.x),
      y: corner.y + inset * (incoming.y + outgoing.y),
    };
  });
}

function inwardNormal(from: OutlinePoint, to: OutlinePoint): OutlinePoint {
  return { x: Math.sign(from.y - to.y), y: Math.sign(to.x - from.x) };
}

function pointKey(x: number, y: number): string {
  return `${x},${y}`;
}

function traceCorners(cells: readonly OutlinePoint[]): readonly OutlinePoint[] {
  const filled = new Set(cells.map(cell => pointKey(cell.x, cell.y)));
  const has = (x: number, y: number): boolean => filled.has(pointKey(x, y));

  const edges = new Map<string, OutlinePoint>();

  for (const { x, y } of cells) {
    if (!has(x, y - 1)) {
      edges.set(pointKey(x, y), { x: x + 1, y });
    }

    if (!has(x + 1, y)) {
      edges.set(pointKey(x + 1, y), { x: x + 1, y: y + 1 });
    }

    if (!has(x, y + 1)) {
      edges.set(pointKey(x + 1, y + 1), { x, y: y + 1 });
    }

    if (!has(x - 1, y)) {
      edges.set(pointKey(x, y + 1), { x, y });
    }
  }

  const startCell = cells.reduce((best, cell) =>
    cell.y < best.y || (cell.y === best.y && cell.x < best.x) ? cell : best,
  );

  const start: OutlinePoint = { x: startCell.x, y: startCell.y };

  // @mutable
  const vertices: OutlinePoint[] = [];

  let current = start;

  do {
    vertices.push(current);

    const next = edges.get(pointKey(current.x, current.y));

    if (!next) {
      break;
    }

    current = next;
  } while (pointKey(current.x, current.y) !== pointKey(start.x, start.y));

  return vertices.filter((vertex, index) => {
    const previous = vertices[(index - 1 + vertices.length) % vertices.length];

    const next = vertices[(index + 1) % vertices.length];

    if (!previous || !next) {
      return true;
    }

    const cross =
      (vertex.x - previous.x) * (next.y - vertex.y) -
      (vertex.y - previous.y) * (next.x - vertex.x);

    return cross !== 0;
  });
}

export const shapeManager = new ShapeManager();
